<script setup lang="ts">
import { copyToClipboard } from '@/utils/apostatudo/clipboard';

const props = defineProps<{
  deposit: DefaultDepositInfoResponse;
}>();

type Emits = {
  'countdown-done': [];
};

const emit = defineEmits<Emits>();

const { isMobile } = useLayout();

const TIME_TO_EXPIRE = 300;

const inputCodeElement = ref<HTMLInputElement | null>(null);
const isBrCodeCopied = ref(false);
const isExpired = ref(false);

async function copyCode() {
  const codeToCopy = props.deposit.br_code || '';

  if (!codeToCopy) return;

  try {
    inputCodeElement.value?.select?.();
  } catch {}

  if (await copyToClipboard(codeToCopy)) {
    isBrCodeCopied.value = true;
    setTimeout(() => (isBrCodeCopied.value = false), 2000);
  }
}

function onTimerEnded() {
  isExpired.value = true;
  emit('countdown-done');
}
</script>

<template>
  <div class="gdm-pix">
    <!-- QR code (somente desktop), acima do conteúdo — como no fluxo antigo -->
    <div
      v-if="!isMobile && deposit.qr_code"
      class="gdm-pix-qr"
    >
      <img
        alt="QR Code PIX"
        :src="deposit.qr_code"
      />
    </div>

    <!-- Copie e Cole: valor + código + copiar -->
    <div class="gdm-pix-box">
      <p class="gdm-pix-value">{{ deposit.amount }}</p>

      <div class="gdm-pix-row">
        <input
          ref="inputCodeElement"
          class="gdm-pix-code"
          readonly
          type="text"
          :value="deposit.br_code"
        />
        <button
          class="gdm-pix-copy"
          type="button"
          @click="copyCode"
        >
          <NuxtIcon :name="isBrCodeCopied ? 'check' : 'copy'" />
          {{
            isBrCodeCopied ? $t('account.wallet.code_copied') : 'Copiar código'
          }}
        </button>
      </div>
    </div>

    <!-- Aviso de CPF do titular -->
    <div class="gdm-pix-warning">
      <NuxtIcon
        class="gdm-pix-warning-icon"
        name="exclamation"
      />
      <span>
        Depósitos são aceitos apenas de contas bancárias com o mesmo CPF do
        titular da conta
      </span>
    </div>

    <!-- Status: aguardando / expirado. O WidgetsCountdown roda escondido só
         pra disparar a expiração (300s) e parar o polling. -->
    <p
      v-if="!isExpired"
      class="gdm-pix-status"
    >
      Aguardando pagamento...
    </p>
    <p
      v-else
      class="gdm-pix-status is-expired"
    >
      {{ $t('deposit.pix_timer_finished') }}
    </p>

    <div class="gdm-pix-timer-driver">
      <WidgetsCountdown
        :total-time="TIME_TO_EXPIRE"
        @timer-ended="onTimerEnded"
      >
        <template #process><span /></template>
        <template #finish><span /></template>
      </WidgetsCountdown>
    </div>
  </div>
</template>

<style scoped lang="scss">
$accent: #0485f7;
$green: #3eff6e;

.gdm-pix {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  font-family: 'Plus Jakarta Sans', sans-serif;
}

.gdm-pix-qr {
  align-self: center;
  padding: 8px;
  background: #fff;
  border: 4px solid #0485f7;
  border-radius: 12px;
  line-height: 0;

  img {
    width: 180px;
    height: 180px;
    max-width: 100%;
    object-fit: contain;
  }
}

.gdm-pix-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 20px;
  border-radius: 12px;
  border: 1px dashed $accent;
  background: rgba(0, 201, 255, 0.05);

  .gdm-pix-value {
    font-size: 24px;
    font-weight: 800;
    line-height: 32px;
    color: $accent;
    text-align: center;
  }

  .gdm-pix-row {
    display: flex;
    gap: 10px;
    align-items: stretch;
    width: 100%;
  }

  .gdm-pix-code {
    flex: 1;
    min-width: 0;
    height: 45px;
    padding: 0 11px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.1);
    color: #fff;
    font-size: 12px;
    text-overflow: ellipsis;
  }

  .gdm-pix-copy {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 9px;
    flex-shrink: 0;
    padding: 0 14px;
    border-radius: 12px;
    background: rgba(4, 133, 247, 0.15);
    color: $accent;
    font-size: 12px;
    cursor: pointer;
    white-space: nowrap;

    :deep(svg) {
      width: 16px;
      height: 18px;
    }
  }
}

.gdm-pix-warning {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 12px 11px;
  border-radius: 12px;
  background: rgba(255, 212, 0, 0.1);

  .gdm-pix-warning-icon {
    flex-shrink: 0;
    color: #ffd400;
    font-size: 18px;
  }

  span {
    font-size: 12px;
    line-height: 16px;
    color: #ffeb87;
  }
}

.gdm-pix-status {
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
  color: $green;
  text-align: center;

  &.is-expired {
    color: #ff5a5a;
  }
}

.gdm-pix-timer-driver {
  height: 0;
  overflow: hidden;
}
</style>
